import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 사이드바 "New" 배지의 근거 — 메뉴별 최근 변경 시각.
 *
 * v1 은 이 배지 하나를 위해 브라우저가 13개 테이블을 각각 조회했다
 * (`Navigation.fetchLatestTableTimestamp` 를 Promise.all 로 13번). 어떤 테이블이 어떤 메뉴에
 * 해당하는지가 화면 코드에 박혀 있었고, 그래서 테이블이 늘 때마다 사이드바를 고쳐야 했다.
 *
 * v2 는 백엔드가 한 번에 계산해 내려준다. 응답은 `메뉴키 → ISO 시각` 이며,
 * 어떤 메뉴에 값이 없으면 최근 변경이 없다는 뜻이다.
 */
export type MenuActivity = Record<string, string>;

export const menuActivityQueryKey = ['menu-activity'] as const;

/** 이 시간 안에 변경된 메뉴에 배지를 붙인다. v1 과 동일하게 24시간. */
export const NEW_BADGE_WINDOW_MS = 24 * 60 * 60 * 1000;

export function useMenuActivity() {
  return useQuery({
    queryKey: menuActivityQueryKey,
    queryFn: ({ signal }) => apiFetch<MenuActivity>('/v1/menus/activity', { signal }),
    // 배지는 부가 정보다. 첫 화면 렌더를 막지 않도록 신선도를 느슨하게 두고 재시도하지 않는다.
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
}

export function isRecent(timestamp: string | null | undefined, now: number): boolean {
  if (!timestamp) return false;
  const time = new Date(timestamp).getTime();
  return Number.isFinite(time) && now - time <= NEW_BADGE_WINDOW_MS;
}
